import { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Mail, Lock, User, Building, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AuthContext } from '@/App';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Refactor Fase 9: signup con Google via Supabase Auth
const handleGoogleSignUp = async () => {
  try {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });
    if (error) console.error('Google OAuth signup error:', error);
  } catch (e) {
    console.error('signInWithOAuth failed:', e);
  }
};

export default function Register() {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    company: ''
  });
  const [loading, setLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Refactor Fase 9: signup con Supabase Auth (email + password + metadata)
  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            name: formData.name,
            company: formData.company,
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) throw error;

      // Si Supabase requiere verificacion (email_confirm habilitado), data.session sera null
      if (!data.session) {
        setRegisteredEmail(formData.email);
        setRegistrationComplete(true);
        toast.success('Cuenta creada! Revisa tu correo para confirmar.');
      } else {
        // Auto-login (email_confirm desactivado en proyecto)
        const accessToken = data.session.access_token;
        try {
          const meRes = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${accessToken}` }
          });
          login(accessToken, meRes.data);
        } catch (_) {
          // Si /auth/me falla (user no esta en tabla custom todavia), seguir con metadata de Supabase
          login(accessToken, {
            id: data.user?.id,
            email: data.user?.email,
            name: formData.name,
            company: formData.company,
            plan: 'free',
          });
        }
        toast.success('Cuenta creada exitosamente!');
        navigate('/dashboard');
      }
    } catch (error) {
      const msg = error?.message || error?.response?.data?.detail || 'Error en el registro. Intenta de nuevo.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Show confirmation screen after registration
  if (registrationComplete) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-hero-glow opacity-30 blur-3xl" />
        
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-card p-8 w-full max-w-md text-center relative z-10"
        >
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-success/20 flex items-center justify-center">
            <CheckCircle className="w-12 h-12 text-success" />
          </div>
          
          <h1 className="font-heading text-2xl font-bold mb-4">¡Revisa tu correo!</h1>
          
          <p className="text-secondary mb-6">
            Hemos enviado un enlace de confirmación a:
          </p>
          
          <p className="font-mono text-ai-glow mb-8 break-all">
            {registeredEmail}
          </p>
          
          <p className="text-secondary text-sm mb-6">
            Haz clic en el enlace del correo para activar tu cuenta y poder iniciar sesión.
          </p>
          
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/login')}
              className="w-full bg-white text-black hover:bg-gray-200 rounded-full"
            >
              Ir al Login
            </Button>
            
            <p className="text-xs text-secondary">
              ¿No recibiste el correo? Revisa tu carpeta de spam o{' '}
              <button 
                onClick={() => {
                  setRegistrationComplete(false);
                  toast.info('Intenta registrarte de nuevo');
                }}
                className="text-ai-glow hover:underline"
              >
                intenta de nuevo
              </button>
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-hero-glow opacity-30 blur-3xl" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="glass-card p-12 w-full max-w-md relative z-10"
      >
        {/* Logo */}
        <div className="flex items-center justify-center space-x-2 mb-8">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-ai-glow to-brand-meta" />
          <span className="font-heading font-bold text-2xl text-primary">AdPulse AI</span>
        </div>

        <h2 className="font-heading font-bold text-3xl text-center mb-2">Create Account</h2>
        <p className="text-secondary text-center mb-8">Start optimizing your ads with AI</p>

        {/* Google Sign Up Button */}
        <Button
          type="button"
          onClick={handleGoogleSignUp}
          className="w-full bg-white text-gray-800 hover:bg-gray-100 rounded-full h-12 text-base font-medium mb-6 flex items-center justify-center gap-3 border border-gray-300"
          data-testid="google-signup-button"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Registrarse con Google
        </Button>

        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-white/10"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-background text-secondary">o regístrate con email</span>
          </div>
        </div>

        <form onSubmit={handleRegister} className="space-y-5">
          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-secondary mb-2 block">
              Full Name
            </label>
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary" />
              <Input
                type="text"
                name="name"
                placeholder="John Doe"
                value={formData.name}
                onChange={handleChange}
                className="bg-white/5 border-white/10 focus:border-white/30 text-white placeholder:text-white/20 rounded-lg h-12 pl-12"
                required
                data-testid="register-name-input"
              />
            </div>
          </div>

          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-secondary mb-2 block">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary" />
              <Input
                type="email"
                name="email"
                placeholder="you@company.com"
                value={formData.email}
                onChange={handleChange}
                className="bg-white/5 border-white/10 focus:border-white/30 text-white placeholder:text-white/20 rounded-lg h-12 pl-12"
                required
                data-testid="register-email-input"
              />
            </div>
          </div>

          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-secondary mb-2 block">
              Company (Optional)
            </label>
            <div className="relative">
              <Building className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary" />
              <Input
                type="text"
                name="company"
                placeholder="Your Company"
                value={formData.company}
                onChange={handleChange}
                className="bg-white/5 border-white/10 focus:border-white/30 text-white placeholder:text-white/20 rounded-lg h-12 pl-12"
                data-testid="register-company-input"
              />
            </div>
          </div>

          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-secondary mb-2 block">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary" />
              <Input
                type="password"
                name="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                className="bg-white/5 border-white/10 focus:border-white/30 text-white placeholder:text-white/20 rounded-lg h-12 pl-12"
                required
                minLength={6}
                data-testid="register-password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-gradient-to-r from-ai-glow to-brand-meta text-white hover:opacity-90 rounded-full h-12 text-base font-medium mt-6"
            disabled={loading}
            data-testid="register-submit-button"
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </Button>
        </form>

        <p className="text-center text-secondary text-sm mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-ai-glow hover:underline" data-testid="register-login-link">
            Login
          </Link>
        </p>

        <button
          onClick={() => navigate('/')}
          className="text-secondary hover:text-primary text-sm mt-4 w-full text-center"
          data-testid="register-back-home-button"
        >
          ← Back to home
        </button>
      </motion.div>
    </div>
  );
}
